'use client'

import { memo } from 'react'
import { useTranslation } from '@/contexts/I18nContext'
import type { NotificationTab, NotificationUnreadData } from '@/lib/api/notification'
import { notificationText } from './styles'

const TABS: Array<{
  tab: NotificationTab
  label: string
  unreadKey: keyof NotificationUnreadData
}> = [
  { tab: 1, label: 'Message.Notification.System', unreadKey: 'system' },
  { tab: 2, label: 'Message.Notification.Personal', unreadKey: 'personal' },
  { tab: 3, label: 'Message.Notification.Interactions', unreadKey: 'interaction' },
]

type Props = {
  variant: 'mobile' | 'desktop'
  tab: NotificationTab
  unread: NotificationUnreadData
  onTabChange: (tab: NotificationTab) => void
}

function MobileTabs({ tab, unread, onTabChange }: Omit<Props, 'variant'>) {
  const { t } = useTranslation('message')

  return (
    <nav className="flex h-[calc(32*var(--vw))] items-stretch">
      {TABS.map((item) => {
        const active = tab === item.tab
        return (
          <button
            key={item.tab}
            type="button"
            className="relative flex h-full flex-1 flex-col items-center justify-end gap-[calc(8*var(--vw))] overflow-visible pb-0"
            onClick={() => onTabChange(item.tab)}
          >
            <span className="inline-flex items-start gap-[calc(4*var(--vw))]">
              <span
                className={`text-[calc(14*var(--vw))] font-normal leading-[calc(20*var(--vw))] ${active ? notificationText.primary : notificationText.inactive}`}
              >
                {t(item.label)}
              </span>
              {unread[item.unreadKey] && (
                <span className="mt-[calc(2*var(--vw))] size-[calc(6*var(--vw))] shrink-0 rounded-full bg-[#FD5251]" />
              )}
            </span>
            <span
              className={`h-[calc(2*var(--vw))] w-[calc(20*var(--vw))] shrink-0 rounded-full ${active ? 'bg-[rgba(255,255,255,0.87)]' : 'bg-transparent'}`}
            />
          </button>
        )
      })}
    </nav>
  )
}

function DesktopTabs({ tab, unread, onTabChange }: Omit<Props, 'variant'>) {
  const { t } = useTranslation('message')

  return (
    <nav className="mt-[20px] flex h-[30px]">
      {TABS.map((item) => {
        const active = tab === item.tab
        return (
          <button
            key={item.tab}
            type="button"
            className={`relative flex flex-1 items-start justify-center text-[14px] leading-[20px] ${active ? 'text-white/90' : 'text-white/45'}`}
            onClick={() => onTabChange(item.tab)}
          >
            <span className="inline-flex items-start gap-[4px]">
              <span>{t(item.label)}</span>
              {unread[item.unreadKey] && (
                <span className="mt-[2px] size-[6px] shrink-0 rounded-full bg-[#FD5251]" />
              )}
            </span>
            {active && (
              <span className="absolute bottom-[-1px] h-[2px] w-[20px] rounded-full bg-white/90" />
            )}
          </button>
        )
      })}
    </nav>
  )
}

export const NotificationTabs = memo(function NotificationTabs({ variant, ...props }: Props) {
  return variant === 'mobile' ? <MobileTabs {...props} /> : <DesktopTabs {...props} />
})
